import React, {
  ChangeEvent,
  memo,
  ReactNode,
  useCallback,
  useState,
} from "react";

import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  Divider,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  EditMessageThreadRemindTimeMutation,
  EditMessageThreadRemindTimeMutationVariables,
} from "gql/graphql";
import EDIT_MESSAGE_THREAD_REMIND_TIME_MUTATION from "modules/messaging/_networking/edit-message-thread-remind-time";
import { useMessageThreadNavigation } from "modules/providers/message-thread-navigation-provider";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import {
  MIXPANEL_EVENT_TYPE,
  PERSON_COUNT_TYPE,
} from "modules/providers/mixpanel-provider/mixpanel-provider";
import MessageThreadToggleReminderToast from "modules/toasts/message-thread-toggle-reminder-toast";
import moment, { Moment } from "moment/moment";
import colors from "app/colors";
import { forceToArray } from "modules/utils/Functions";
import {
  HourGlassIcon,
  RewindTimeIcon,
  BriefCaseIcon,
  SunIcon,
  CalendarWeekIcon,
} from "modules/icons";
import useSearchFilter from "modules/hooks/use-search-filter";
import SEARCH_MESSAGE_THREADS_QUERY from "modules/messaging/_networking/search-message-threads";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_THREAD_SEARCH_FILTERS,
} from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider/overlay-provider";
import useQueryString from "modules/hooks/use-query-string";
import useDisplayToast from "modules/hooks/use-display-toast";

interface IProps {
  messageThreadId: string;
}

const MessageThreadCreateReminderModal = ({ messageThreadId }: IProps) => {
  const { closeModal } = useOverlay();
  const [showCustom, setShowCustom] = useState(false);
  const [customDatetime, setCustomDatetime] = useState<Moment>();
  const { displayToast } = useDisplayToast();
  const { qsMessageThreadId } = useQueryString();
  const { track, incrementPersonProperty } = useMixpanel();
  const { advanceToNextAvailableMessageThread } = useMessageThreadNavigation();
  const { searchFilters } = useSearchFilter();

  const [setMessageThreadReminderAPI] = useMutation<
    EditMessageThreadRemindTimeMutation,
    EditMessageThreadRemindTimeMutationVariables
  >(EDIT_MESSAGE_THREAD_REMIND_TIME_MUTATION);

  const onSetRemindTime = useCallback(
    (remindTime: Moment | undefined, timeframe: string) => () => {
      if (remindTime) {
        setMessageThreadReminderAPI({
          variables: {
            id: messageThreadId,
            remindTime: remindTime.toISOString(),
          },
          optimisticResponse: {
            editMessageThread: {
              __typename: "MessageThread",
              id: messageThreadId,
              remindTime: remindTime.toISOString(),
              isInbox: false,
            },
          },
          refetchQueries: [
            {
              query: SEARCH_MESSAGE_THREADS_QUERY,
              variables: {
                input: {
                  pageSize: DEFAULT_PAGE_SIZE,
                  searchFilterList: [
                    ...forceToArray(DEFAULT_THREAD_SEARCH_FILTERS),
                    ...searchFilters,
                  ],
                },
              },
            },
          ],
        });

        track(MIXPANEL_EVENT_TYPE.CONVERSATION_INBOX_REMINDER_TOGGLED, {
          "Reminder Set": true,
          "Reminder Timeframe": timeframe,
        });
        incrementPersonProperty(PERSON_COUNT_TYPE.REMINDER_COUNT);

        displayToast(() => (
          <MessageThreadToggleReminderToast remindTime={remindTime} />
        ));

        if (qsMessageThreadId === messageThreadId) {
          advanceToNextAvailableMessageThread();
        }

        closeModal();
      }
    },
    [
      setMessageThreadReminderAPI,
      messageThreadId,
      searchFilters,
      track,
      incrementPersonProperty,
      displayToast,
      qsMessageThreadId,
      closeModal,
      advanceToNextAvailableMessageThread,
    ],
  );

  const onCustomOptionClick = useCallback(() => {
    setShowCustom(true);
  }, []);

  const onCustomSaveClick = useCallback(() => {
    onSetRemindTime(customDatetime, "Custom")();
  }, [customDatetime, onSetRemindTime]);

  const onCustomDateTimeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newDateTime = moment(e.currentTarget.value);
      setCustomDatetime(newDateTime.isValid() ? newDateTime : undefined);
    },
    [],
  );

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text>Remind Me</Text>
          </Center>
          <ModalCloseButton color={colors.grayMain} />
        </ModalHeader>
        <ModalBody>
          <Stack w="full" marginBottom={5}>
            <ButtonRow
              icon={<HourGlassIcon boxSize={6} />}
              title="In an hour"
              remindTime={moment().add(1, "hour")}
              deltaFormat="h:mm A"
              onClick={onSetRemindTime(moment().add(1, "hour"), "Hour")}
            />
            <Divider />
            <ButtonRow
              icon={<RewindTimeIcon boxSize={6} />}
              title="In a few hours"
              remindTime={moment().add(3, "hours")}
              deltaFormat="h:mm A"
              onClick={onSetRemindTime(moment().add(3, "hours"), "Few Hours")}
            />
            <Divider />
            <ButtonRow
              icon={<SunIcon boxSize={6} />}
              title="Tomorrow"
              remindTime={moment()
                .startOf("day")
                .add(1, "days")
                .add(9, "hours")}
              deltaFormat="dddd h:mm A"
              onClick={onSetRemindTime(
                moment().startOf("day").add(1, "days").add(9, "hours"),
                "Tomorrow",
              )}
            />
            <Divider />
            <ButtonRow
              icon={<BriefCaseIcon boxSize={6} />}
              title="Next Week"
              remindTime={moment()
                .startOf("week")
                .add(1, "week")
                .add(1, "day")
                .add(9, "hours")}
              deltaFormat="dddd h:mm A"
              onClick={onSetRemindTime(
                moment()
                  .startOf("week")
                  .add(1, "week")
                  .add(1, "day")
                  .add(9, "hours"),
                "Next Week",
              )}
            />
            <Divider />
            {!showCustom && (
              <ButtonRow
                icon={<CalendarWeekIcon boxSize={6} />}
                title="Custom"
                onClick={onCustomOptionClick}
              />
            )}
            {showCustom && (
              <HStack>
                <Input
                  placeholder="Select Date and Time"
                  size="md"
                  type="datetime-local"
                  onChange={onCustomDateTimeChange}
                />
                <Button
                  variant="solid"
                  isDisabled={
                    customDatetime === undefined ||
                    !customDatetime.isValid() ||
                    customDatetime < moment()
                  }
                  onClick={onCustomSaveClick}
                >
                  Save
                </Button>
              </HStack>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface IButtonRowProps {
  icon: ReactNode;
  title: string;
  remindTime?: Moment;
  deltaFormat?: string;
  onClick: () => void;
}

const ButtonRow = ({
  icon,
  title,
  remindTime,
  deltaFormat,
  onClick,
}: IButtonRowProps) => (
  <Button variant="link" flex={1} onClick={onClick}>
    <HStack w="full">
      {icon}
      <Text flex={1} textAlign="left">
        {title}
      </Text>
      {remindTime && deltaFormat && (
        <Text fontSize="xs" color={colors.grayMain}>
          {remindTime.format(deltaFormat)}
        </Text>
      )}
    </HStack>
  </Button>
);

export default memo(MessageThreadCreateReminderModal);
